import React, { useMemo } from 'react';

/**
 * Signal over time for a handful of tracked networks.
 *
 * The spectrum view answers "what is on the air", which is a question you ask
 * once. This answers "is it getting stronger", which is the question you ask
 * while walking — and it is the only view that locates anything physically. A
 * rogue AP is found by watching a line climb as you move toward it, not by
 * reading a table.
 *
 * Deliberately limited to a few series. Every network in range plotted at once
 * is the mistake the spectrum view made: in a branch office that is a hundred
 * lines, and a hundred lines is a texture, not a measurement.
 */

const FLOOR_DBM = -95;
const CEIL_DBM = -30;

const VIEW_W = 1000;
const VIEW_H = 260;
const PAD = { top: 16, right: 132, bottom: 26, left: 40 };

// Distinct hues rather than security colours: here the series identity is what
// matters, and two WPA2 networks must not be the same line.
const PALETTE = [
  '#38bdf8', '#f472b6', '#34d399', '#fbbf24',
  '#a78bfa', '#fb7185', '#22d3ee', '#facc15',
];

const SignalTimeline = ({
  history = {},
  accessPoints = [],
  tracked = [],
  onUntrack,
  depth = 90,
  intervalMs = 2000,
}) => {
  const apByBssid = useMemo(() => {
    const out = {};
    accessPoints.forEach((ap) => { out[ap.bssid] = ap; });
    return out;
  }, [accessPoints]);

  const series = useMemo(() => tracked
    .map((bssid, i) => {
      const points = history[bssid] || [];
      const ap = apByBssid[bssid];
      return {
        bssid,
        colour: PALETTE[i % PALETTE.length],
        points,
        label: ap?.hidden ? '‹hidden›' : (ap?.ssid || bssid),
        current: points.length ? points[points.length - 1] : null,
      };
    })
    .filter((s) => s.points.length > 0), [tracked, history, apByBssid]);

  const plotW = VIEW_W - PAD.left - PAD.right;
  const plotH = VIEW_H - PAD.top - PAD.bottom;

  const yFor = (dbm) => {
    const clamped = Math.max(FLOOR_DBM, Math.min(CEIL_DBM, dbm ?? FLOOR_DBM));
    return PAD.top + (1 - (clamped - FLOOR_DBM) / (CEIL_DBM - FLOOR_DBM)) * plotH;
  };

  // Newest sample sits at the right edge. A series that started late is shorter
  // than the window, so it is right-aligned rather than stretched — otherwise a
  // network seen twice would draw across three minutes it was never observed in.
  const xFor = (indexFromEnd) =>
    PAD.left + plotW - (indexFromEnd / Math.max(1, depth - 1)) * plotW;

  const windowSeconds = Math.round((depth * intervalMs) / 1000);
  const timeTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    x: PAD.left + plotW * (1 - f),
    label: f === 0 ? 'now' : `-${Math.round(windowSeconds * f)}s`,
  }));

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1 px-1">
        <span className="text-xs font-medium text-slate-400">
          Signal over time
        </span>
        <span className="text-[11px] text-slate-600">
          {series.length
            ? `${series.length} tracked · last ${windowSeconds}s`
            : 'select a network below to track it'}
        </span>
      </div>

      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="w-full h-[260px] select-none">
        {/* dBm gridlines. -70 is solid: it is roughly the usable-signal boundary
            and gives the eye a fixed reference as lines move. */}
        {[-40, -50, -60, -70, -80, -90].map((dbm) => (
          <g key={dbm}>
            <line
              x1={PAD.left} x2={PAD.left + plotW}
              y1={yFor(dbm)} y2={yFor(dbm)}
              className="stroke-slate-800" strokeWidth="1"
              strokeDasharray={dbm === -70 ? '0' : '3 5'}
            />
            <text
              x={PAD.left - 6} y={yFor(dbm) + 3} textAnchor="end"
              className="fill-slate-600" style={{ fontSize: '9px' }}
            >
              {dbm}
            </text>
          </g>
        ))}

        {timeTicks.map((tick) => (
          <text
            key={tick.label}
            x={tick.x} y={VIEW_H - 8} textAnchor="middle"
            className="fill-slate-600" style={{ fontSize: '9px' }}
          >
            {tick.label}
          </text>
        ))}

        {/* 'now' edge — the line everything scrolls towards */}
        <line
          x1={PAD.left + plotW} x2={PAD.left + plotW}
          y1={PAD.top} y2={PAD.top + plotH}
          className="stroke-slate-700" strokeWidth="1"
        />

        {series.map((s) => {
          const pts = s.points
            .map((dbm, i) => {
              const fromEnd = s.points.length - 1 - i;
              return `${xFor(fromEnd)},${yFor(dbm)}`;
            })
            .join(' ');

          const lastY = yFor(s.current);
          return (
            <g key={s.bssid}>
              <polyline
                points={pts}
                fill="none"
                stroke={s.colour}
                strokeWidth="1.75"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              <circle cx={PAD.left + plotW} cy={lastY} r="3" fill={s.colour} />
              <text
                x={PAD.left + plotW + 8}
                y={lastY + 3}
                className="fill-slate-300"
                style={{ fontSize: '10px' }}
              >
                {s.label.slice(0, 16)}
                <tspan className="fill-slate-500" style={{ fontSize: '9px' }}>
                  {'  '}{s.current}
                </tspan>
              </text>
            </g>
          );
        })}

        {series.length === 0 && (
          <text
            x={PAD.left + plotW / 2} y={VIEW_H / 2} textAnchor="middle"
            className="fill-slate-700" style={{ fontSize: '11px' }}
          >
            no networks tracked — click a row in the table below
          </text>
        )}
      </svg>

      {series.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 px-1">
          {series.map((s) => (
            <button
              key={s.bssid}
              onClick={() => onUntrack && onUntrack(s.bssid)}
              className="flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-300 transition-colors"
              title="Stop tracking"
            >
              <span
                className="inline-block w-3 h-[3px] rounded-full"
                style={{ backgroundColor: s.colour }}
              />
              {s.label.slice(0, 20)}
              <span className="text-slate-700">×</span>
            </button>
          ))}
          <span className="text-[11px] text-slate-600 ml-auto">
            Rising means you are moving towards the transmitter
          </span>
        </div>
      )}
    </div>
  );
};

export default SignalTimeline;
